import { useEffect, useState } from 'react';
import { Bell, Check, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

type NotificationRecord = {
  id: string;
  type: 'new_resource' | 'data_updated' | 'community_post';
  title: string;
  body: string;
  href?: string;
  read?: boolean;
  createdAt?: { toDate?: () => Date } | string;
};

function createdAtMs(value: NotificationRecord['createdAt']): number {
  if (value && typeof value === 'object' && typeof value.toDate === 'function') return value.toDate().getTime();
  const parsed = Date.parse(String(value || ''));
  return Number.isFinite(parsed) ? parsed : 0;
}

function relativeTime(value: NotificationRecord['createdAt']): string {
  const elapsed = Math.max(0, Date.now() - createdAtMs(value));
  const minutes = Math.floor(elapsed / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function NotificationBell() {
  const { userProfile } = useAuth();
  const { t } = useI18n();
  const [notifications, setNotifications] = useState<NotificationRecord[]>([]);

  useEffect(() => {
    if (!userProfile?.uid) {
      setNotifications([]);
      return;
    }
    let disposed = false;
    let unsubscribe: (() => void) | undefined;
    void Promise.all([import('@/lib/firebase'), import('firebase/firestore')]).then(([{ db }, firestoreSdk]) => {
      if (disposed) return;
      const notificationsQuery = firestoreSdk.query(
        firestoreSdk.collection(db, 'users', userProfile.uid, 'notifications'),
        firestoreSdk.limit(25),
      );
      unsubscribe = firestoreSdk.onSnapshot(
        notificationsQuery,
        (snapshot) => {
          setNotifications(
            snapshot.docs
              .map((notification) => ({ id: notification.id, ...notification.data() }) as NotificationRecord)
              .sort((a, b) => createdAtMs(b.createdAt) - createdAtMs(a.createdAt)),
          );
        },
        (error) => {
          console.warn('[Notifications] Subscription unavailable', error);
          setNotifications([]);
        },
      );
    });

    return () => {
      disposed = true;
      unsubscribe?.();
    };
  }, [userProfile?.uid]);

  if (!userProfile?.uid) return null;

  const unreadCount = notifications.filter((notification) => !notification.read).length;
  const markRead = async (notificationId: string) => {
    const [{ db }, firestoreSdk] = await Promise.all([import('@/lib/firebase'), import('firebase/firestore')]);
    await firestoreSdk.updateDoc(firestoreSdk.doc(db, 'users', userProfile.uid, 'notifications', notificationId), {
      read: true,
      readAt: new Date().toISOString(),
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={t('notifications.label')}
          title={t('notifications.label')}
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span
              aria-label={`${unreadCount} ${t('notifications.unread')}`}
              className="absolute -right-0.5 -top-0.5 flex min-h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[min(23rem,calc(100vw-2rem))]">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>{t('notifications.title')}</span>
          {unreadCount > 0 && (
            <span className="text-xs text-muted-foreground">
              {unreadCount} {t('notifications.unread')}
            </span>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {notifications.length === 0 && (
          <div className="px-2 py-6 text-center text-sm text-muted-foreground">{t('notifications.empty')}</div>
        )}
        {notifications.map((notification) => (
          <DropdownMenuItem
            key={notification.id}
            asChild
            onSelect={() => void (!notification.read && markRead(notification.id))}
          >
            <Link to={notification.href || '/'} className="flex cursor-pointer items-start gap-3 p-3">
              <span
                className={`mt-1 h-2 w-2 shrink-0 rounded-full ${notification.read ? 'bg-muted' : 'bg-primary'}`}
                aria-hidden="true"
              />
              <span className="min-w-0 flex-1">
                <span className="flex items-center justify-between gap-2">
                  <span className="truncate font-semibold">{notification.title}</span>
                  {!notification.read && <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />}
                </span>
                <span className="mt-1 block line-clamp-2 text-xs text-muted-foreground">{notification.body}</span>
                <span className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground">
                  <ExternalLink className="h-3 w-3" aria-hidden="true" />
                  {relativeTime(notification.createdAt)}
                </span>
              </span>
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
